import { useCallback, useRef, useState } from 'react';

import { useDeviceType } from '@packages/shared';
import type { NavigationItem } from '../navigationTypes';
import { getNavigationItemKey } from '../getNavigationItemKey';

export type TreeNavigationDirection = 'leaf' | 'root' | 'lateral';

export type UseTreeNavigationPopupOptions = {
  /**
   * Interaction mode with the main navigation
   *
   * @default 'click'
   */
  mode?: 'click' | 'hover';
  /** Fired when the user first interacts with the nav bar */
  onFirstInteraction?: () => void;
};

/**
 * Hook for managing the state of a tree navigation popup, and all related logic and workarounds.
 */
export const useTreeNavigationPopup = ({
  mode = 'click',
  onFirstInteraction,
}: UseTreeNavigationPopupOptions = {}) => {
  const { isDesktop } = useDeviceType();

  const defaultNavigationDirection = isDesktop ? 'lateral' : 'leaf';

  const [navigationDirection, setNavigationDirection] = useState<TreeNavigationDirection>(
    defaultNavigationDirection,
  );
  const [selectedKey, setSelectedKey] = useState<string>();
  const [showPopup, setShowPopup] = useState(false);

  const parentStack = useRef<string[]>([]);

  const enableClickAwayWorkaround = mode === 'click' && isDesktop;

  // A click on a different item from the one currently open executes both the item onClick, as well as the onClickAway handler, in this order.
  // This leads to the newly opened popup immediately closing again.
  // This ref is used as a workaround to prevent the onClickAway handler from executing in this specific case.
  const preventNextClose = useRef(false);

  const closePopup = useCallback(() => {
    if (enableClickAwayWorkaround && preventNextClose.current) {
      preventNextClose.current = false;
      return;
    }

    setNavigationDirection(defaultNavigationDirection);
    setSelectedKey(undefined);
    setShowPopup(false);
  }, [enableClickAwayWorkaround, defaultNavigationDirection]);

  const isSelected = (item: NavigationItem) => getNavigationItemKey(item) === selectedKey;

  const isFirstInteraction = useRef(true);

  const selectMainItem = (item: NavigationItem, preventToggle = false) => {
    if (isFirstInteraction.current) {
      isFirstInteraction.current = false;
      onFirstInteraction?.();
    }

    const isAlreadyOpen = showPopup && isSelected(item);

    if (enableClickAwayWorkaround) {
      preventNextClose.current = true;
    }

    if ((isAlreadyOpen && !preventToggle) || !item.children.length) {
      closePopup();
      return;
    }

    parentStack.current = [];

    setNavigationDirection(defaultNavigationDirection);
    setSelectedKey(getNavigationItemKey(item));
    setShowPopup(true);
  };

  const selectPopupItem = (item: NavigationItem) => {
    if (!selectedKey || !item.children.length) {
      closePopup();
      return;
    }

    parentStack.current.push(selectedKey);
    setNavigationDirection('leaf');
    setSelectedKey(getNavigationItemKey(item));
  };

  const backToParent = () => {
    const parentKey = parentStack.current.pop();
    if (parentKey) {
      setNavigationDirection('root');
      setSelectedKey(parentKey ?? null);
    } else {
      closePopup();
    }
  };

  const isRoot = (item: NavigationItem) =>
    getNavigationItemKey(item) === (parentStack.current[0] ?? selectedKey);

  return {
    navigationDirection,
    showPopup,
    isSelected,
    isRoot,
    selectMainItem,
    selectPopupItem,
    backToParent,
    /** Memoized function, safe to use as a useEffect dependency */
    closePopup,
  };
};
